import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { StatusBadge, PersonaBadge } from "@/components/Badges";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Copy, Loader2, Upload, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

export default function TaskDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [proofUrl, setProofUrl] = useState("");
  const [file, setFile] = useState(null);

  const q = useQuery({
    queryKey: ["task", id],
    queryFn: () => api.get(`/tasks/${id}`).then(r => r.data),
    refetchInterval: 5000,
  });
  const t = q.data;

  const submit = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("proof_url", proofUrl);
      fd.append("screenshot", file);
      return api.post(`/tasks/${id}/submit-proof`, fd, { headers: { "Content-Type": "multipart/form-data" }});
    },
    onSuccess: (r) => {
      const st = r.data.status;
      if (st === "Completed") toast.success("Verified ✓ Task complete");
      else toast.error(`Verification failed: ${r.data.verification_note || "check note"}`);
      qc.invalidateQueries({queryKey:["task", id]});
      qc.invalidateQueries({queryKey:["agent-tasks"]});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Submission failed"),
  });

  if (q.isLoading || !t) return <Layout title="Task"><div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div></Layout>;

  const canSubmit = t.status === "Awaiting Execution" || t.status === "Verification Failed";
  const copyScript = () => {
    navigator.clipboard.writeText(t.task_script || "");
    toast.success("Script copied");
  };

  return (
    <Layout
      subtitle={<Link to="/agent" className="hover:text-white flex items-center gap-1"><ArrowLeft className="w-3 h-3"/>Back to board</Link>}
      title="Task Brief."
      right={<StatusBadge status={t.status}/>}
    >
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {/* Meta */}
          <div className="border border-white/10 p-5 bg-[#09090B]">
            <div className="flex items-center gap-3 mb-3">
              <PersonaBadge persona={t.persona_angle}/>
              <div className="font-mono text-[11px] text-zinc-500">Action:</div>
              <div className="text-[13px] text-white">{t.action_type}</div>
              <div className="ml-auto font-mono text-[11px] text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3"/> +{t.execution_delay_minutes}m</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Target subreddit</div>
                <div className="font-mono text-[13px] text-[#FF3B30]">{t.subreddit}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Target thread</div>
                <a href={t.target_url} target="_blank" rel="noreferrer" data-testid="target-url-link" className="font-mono text-[13px] text-zinc-200 hover:text-white hover:underline flex items-center gap-1 truncate">
                  Open <ExternalLink className="w-3 h-3"/>
                </a>
              </div>
            </div>
            {t.notes && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Persona guidance</div>
                <div className="text-[13px] text-zinc-300 leading-relaxed">{t.notes}</div>
              </div>
            )}
          </div>

          {/* Script */}
          {t.task_script ? (
            <div className="border border-white/10 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Copy-paste script</div>
                <button onClick={copyScript} data-testid="copy-script-btn" className="border border-white/10 hover:border-white/30 px-2 py-1 text-[11px] font-mono flex items-center gap-1.5">
                  <Copy className="w-3 h-3"/> COPY
                </button>
              </div>
              <div className="bg-[#050505] border border-white/10 p-4 text-[13px] whitespace-pre-wrap leading-relaxed" data-testid="task-script-text">
                {t.task_script}
              </div>
            </div>
          ) : (
            <div className="border border-white/10 p-5 text-[13px] text-zinc-400">
              <em>No script — action-only task ({t.action_type}). Execute directly on the target thread.</em>
            </div>
          )}

          {/* Verification note (if failed) */}
          {t.status === "Verification Failed" && t.verification_note && (
            <div className="border border-[#FF3B30]/50 p-5 scanlines" data-testid="verification-failed-note">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-[#FF3B30]"/>
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF3B30]">Verification Failed</div>
              </div>
              <div className="font-mono text-[12px] text-zinc-300 whitespace-pre-wrap">// {t.verification_note}</div>
            </div>
          )}

          {t.status === "Completed" && (
            <div className="border border-emerald-500/40 p-5 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400"/>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">Verified</div>
                <div className="text-[13px] text-zinc-300 mt-1">{t.verification_note}</div>
              </div>
            </div>
          )}
        </div>

        {/* Submit panel */}
        <div className="border border-white/10 p-5 h-fit sticky top-24">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Submit proof</div>
          <h3 className="font-display font-black text-xl tracking-tight mb-4">Verify execution.</h3>

          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Executed URL</label>
          <input
            type="url"
            value={proofUrl}
            onChange={(e)=>setProofUrl(e.target.value)}
            placeholder="https://reddit.com/r/.../comments/..."
            data-testid="proof-url-input"
            disabled={!canSubmit}
            className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-[12px] font-mono disabled:opacity-50"
          />

          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Screenshot</label>
          <label className="block border border-dashed border-white/15 hover:border-white/30 p-4 cursor-pointer mb-4">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e)=>setFile(e.target.files?.[0] || null)}
              data-testid="proof-file-input"
              disabled={!canSubmit}
              className="hidden"
            />
            <div className="flex items-center gap-2 text-[12px] text-zinc-400">
              <Upload className="w-4 h-4"/>
              <span className="truncate">{file ? file.name : "Choose PNG / JPEG / WEBP"}</span>
            </div>
          </label>

          <button
            onClick={() => {
              if (!proofUrl || !file) { toast.error("Provide URL and screenshot"); return; }
              submit.mutate();
            }}
            disabled={submit.isPending || !canSubmit}
            data-testid="submit-proof-btn"
            className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] disabled:opacity-40 text-white font-medium py-2.5 flex items-center justify-center gap-2"
          >
            {submit.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
            {submit.isPending ? "VERIFYING…" : "SUBMIT FOR AI VERIFICATION"}
          </button>

          {!canSubmit && (
            <div className="text-[11px] font-mono text-zinc-500 mt-3">
              {t.status === "Completed" ? "Task complete." : "Not submittable in current state."}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
